/**
 * MobileVentrTopMenu
 *
 * Mobile-optimized top navigation for VentrView.
 * Replaces the desktop top menu when isMobile === 2.
 * Uses MUI Tabs for view switching (À venir / Terminés) and MUI IconButton for export.
 */
import React from "react";
import {
  Box,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import Langs from "../../../app/lang/langs";

/* ------------------------------------------------------------------ */
/*  Shared style constants                                             */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.28s cubic-bezier(0.4, 0, 0.2, 1)";

const iconButtonSx = {
  minWidth: 44,
  minHeight: 44,
  borderRadius: "12px",
  color: "rgba(0,0,0,0.7)",
  transition: TRANSITION_SMOOTH,
  "&:hover": {
    backgroundColor: "rgba(255,144,21,0.08)",
    color: "rgb(255,144,21)",
    boxShadow: "0 2px 8px rgba(255,144,21,0.15)",
    transform: "scale(1.06)",
  },
  "&:active": {
    transform: "scale(0.96)",
  },
};

/**
 * @param {Object} props
 * @param {Array}  props.viewsOptions - [{label, value}, ...]
 * @param {number|string} props.selectedView
 * @param {Function} props.onViewChange
 * @param {Function} props.onExport
 * @param {boolean}  props.isCollab - true when user is a collaborator (no tabs, no export)
 */
export default function MobileVentrTopMenu({
  viewsOptions,
  selectedView,
  onViewChange,
  onExport,
  isCollab,
}) {
  const handleTabChange = (_event, newValue) => {
    onViewChange(newValue);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        bgcolor: "rgba(255,255,255,0.78)",
        backdropFilter: "blur(20px) saturate(1.4)",
        WebkitBackdropFilter: "blur(20px) saturate(1.4)",
        borderBottom: "1px solid rgba(0,40,128,0.06)",
        boxShadow:
          "0 4px 6px -4px rgba(0,40,128,0.05), 0 2px 3px -2px rgba(0,0,0,0.03)",
        px: 1.5,
        pt: 0.75,
        pb: 0,
        transition: TRANSITION_SMOOTH,
        position: "relative",
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: "1px",
          background:
            "linear-gradient(90deg, transparent, rgba(255,144,21,0.15), transparent)",
        },
      }}
    >
      {/* Title row with action buttons */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          minHeight: 48,
          px: 0.5,
        }}
      >
        <Box
          sx={{
            fontWeight: 700,
            fontSize: "1.15rem",
            color: "rgb(0,0,0)",
            pl: 0.5,
            letterSpacing: "-0.025em",
            transition: "color 0.2s ease",
          }}
        >
          <Langs str="Entretiens" />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {/* Export button - hidden for collab role */}
          {!isCollab && (
            <Tooltip title={<Langs str="Export" />}>
              <IconButton
                onClick={onExport}
                size="large"
                sx={iconButtonSx}
              >
                <FileDownloadOutlinedIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Tabs row for view switching - hidden for collab role */}
      {!isCollab && (
        <Tabs
          value={Number(selectedView) || 0}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            minHeight: 40,
            "& .MuiTab-root": {
              minHeight: 40,
              minWidth: 0,
              px: 1.5,
              py: 0.5,
              fontSize: "0.8rem",
              fontWeight: 600,
              textTransform: "none",
              color: "rgba(255,144,21,0.55)",
              borderRadius: "10px 10px 0 0",
              transition: TRANSITION_SMOOTH,
              "&:hover": {
                color: "rgba(255,144,21,0.8)",
                backgroundColor: "rgba(255,144,21,0.06)",
              },
              "&.Mui-selected": {
                fontWeight: 700,
                color: "rgb(255,144,21)",
              },
            },
            "& .MuiTabs-indicator": {
              height: 3,
              borderRadius: "3px 3px 0 0",
              background: "linear-gradient(90deg, #FF8701, #FFC100)",
              boxShadow: "0 0 8px rgba(255,144,21,0.3)",
              transition: "all 350ms cubic-bezier(0.4, 0, 0.2, 1)",
            },
          }}
        >
          {viewsOptions.map((option) => (
            <Tab
              key={option.value}
              label={option.label}
              value={option.value}
            />
          ))}
        </Tabs>
      )}
    </Box>
  );
}
